// rules.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../fonts/breuer-headline.css';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { resolveGameEntryRoute } from '../../functions/stageReportSync';
import logoImg from '../../img/logo.png';
import bgImg from '../../img/background.png';
import bgImgMobile from '../../img/mob.png';

/** Thin line icons for the mission-rules list, matching the reference art —
 *  drawn inline instead of adding new image assets. */
const RULE_ICON_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function ClockIcon() {
  return (
    <svg {...RULE_ICON_PROPS}>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l3 2" />
      <path d="M9.5 2h5" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg {...RULE_ICON_PROPS}>
      <path d="M8 7l1.4-2h5.2L16 7" />
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <circle cx="12" cy="13.5" r="3.5" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg {...RULE_ICON_PROPS}>
      <circle cx="9" cy="8" r="3" />
      <path d="M4 20c0-3 2.2-5 5-5s5 2 5 5" />
      <circle cx="17.5" cy="9.5" r="2.2" />
      <path d="M15 20c0-2.3 1.5-4 3.5-4" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg {...RULE_ICON_PROPS}>
      <path d="M8 4h8v4a4 4 0 0 1-8 0V4z" />
      <path d="M8 5H5.5A2.5 2.5 0 0 0 8 9" />
      <path d="M16 5h2.5A2.5 2.5 0 0 1 16 9" />
      <path d="M12 12v3" />
      <path d="M9 19h6" />
      <path d="M10.2 15h3.6l0.7 4h-5l0.7-4z" />
    </svg>
  );
}

const RULE_ITEMS = [
  {
    icon: ClockIcon,
    text: (
      <>You have <strong>20 minutes</strong> only until the room fills up with water</>
    ),
  },
  {
    icon: CameraIcon,
    text: "Ravi sends you pictures of the different lockers and doors inside the vault",
  },
  {
    icon: PeopleIcon,
    text: (
      <>
        Live the customer centric values while guiding <strong>Ravi</strong> to
        identify clues inside the room, in order to escape in time
      </>
    ),
  },
  {
    icon: TrophyIcon,
    text: "Those who manage to do it the quickest win this challenge",
  },
];

/** Per-step stepper label and card heading (title + accented word). */
const STEPS = [
  { label: "Intro Story", title: "A Late", accent: "Visit" },
  { label: "The Situation", title: "Trapped in the", accent: "Vault" },
  { label: "Rules", title: "Mission", accent: "Rules" },
];

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

/** Mirrors the other pages' mobile-portrait breakpoint, used to pick mob.png. */
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

/** Redux's auth.user is only ever populated by Login's own mount effect, so a
 *  hard refresh (or deep link) straight into /rules leaves it null forever —
 *  fall back to the same sessionStorage userData thankyou.jsx already reads. */
function readSessionUser() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("userData") || "{}");
    if (raw && typeof raw === "object" && (raw.userId || raw.userid || raw.id)) {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Mirrors old/rules1-4.php's shared right-click/devtools-shortcut block. */
function useDisableInspect() {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockShortcutKeys = (e) => {
      if (e.keyCode === 123) return false; // F12
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) return false; // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) return false; // Ctrl+Shift+J
      if (e.ctrlKey && e.keyCode === 85) return false; // Ctrl+U
      return true;
    };
    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('keydown', blockShortcutKeys);
    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('keydown', blockShortcutKeys);
    };
  }, []);
}

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const { user: reduxUser } = useSelector((state) => state.auth);
  const user = useMemo(() => reduxUser || readSessionUser(), [reduxUser]);
  const adminToken = useSelector(selectAdminToken);
  const isMobilePortrait = useMobilePortrait();
  const [isLoading, setIsLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  // old/rules1.php -> rules2.php -> rules4.php (rules3.php's forward link was
  // never wired up in old/*, so this mirrors the flow players actually took).
  const [step, setStep] = useState(1);

  useDisableInspect();

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;

    // Welcome / full-house themes have no quiz config (no noOfQuestion) — skip setUserQuestions.
    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;

    setIsLoading(true);

    if (!needsQuizInit) {
      return;
    }

    dispatch(processQuestions())
      .unwrap()
      .then(() => {
        return initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        });
      })
      .catch((err) => {
        console.error('Error in process→init chain:', err);
      });
  }, [themeData, user, adminToken, dispatch]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  // Paint the background on <body> (not a page div) so it extends behind the
  // shared header too, instead of leaving the old theme background showing
  // through there while a separate div covers the rest of the page.
  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = `url("${isMobilePortrait ? bgImgMobile : bgImg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, [isMobilePortrait]);

  const goToGame = useCallback(async () => {
    if (!user || nextLoading) return;

    let storedUser = user;
    try {
      storedUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
    } catch {
      storedUser = user;
    }

    setNextLoading(true);
    try {
      const route = await resolveGameEntryRoute({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass: storedUser.sessionId === "demobypass",
      });
      navigate(route, { replace: true });
    } catch (err) {
      console.error("rules goToGame:", err);
      navigate("/game", { replace: true });
    } finally {
      setNextLoading(false);
    }
  }, [user, nextLoading, backendBase, adminToken, navigate]);

  const handleNextClick = (e) => {
    e.preventDefault();
    if (step < 3) {
      setStep((s) => s + 1);
      return;
    }
    goToGame();
  };

  if (!isLoading) {
    return (
      <div className='quiz-loader-container'>
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const { label: stepLabel, title: stepTitle, accent: stepAccent } =
    STEPS[step - 1];

  return (
    <div className="user-rules-page">
      <div className="rules-header">
        <img src={logoImg} className="rules-logo" alt="Escape the Vault" />
      </div>

      {/* key={step} remounts the card so each step fades in. */}
      <div className="rules-content" key={step}>
        <ol className="rules-stepper" aria-label={`Step ${step} of ${STEPS.length}`}>
          {STEPS.map((s, i) => {
            const n = i + 1;
            const state = n < step ? " is-done" : n === step ? " is-active" : "";
            return (
              <li key={s.label} className={`rules-stepper__item${state}`}>
                <span className="rules-stepper__dot">{n < step ? "✓" : n}</span>
                <span className="rules-stepper__label">{s.label}</span>
              </li>
            );
          })}
        </ol>

        <p className="rules-eyebrow-label">{stepLabel}</p>
        <h2 className="rules-main-heading">
          {stepTitle} <span className="rules-main-heading__accent">{stepAccent}</span>
        </h2>

        <div className="rules-body">
          {step === 1 && (
            <>
              <p className="rules-text">
                Your customer, <strong>Ravi</strong>, visited you at your branch for a
                critical document submission that rests in his safety deposit box at
                the vault. It was 6 pm in the evening and all the other employees had
                left for the day.
              </p>
              <p className="rules-text rules-text--lead">
                You bring him to the vault in which one of the safety boxes belongs
                to Ravi.
              </p>
              <p className="rules-text">
                You soon realize that you have left the key to the locker outside the
                room and quickly step out to grab it. Ravi is now inside alone and
                presses a wrong switch by error.
              </p>
              <p className="rules-callout rules-callout--alert">
                <span className="rules-callout__icon" aria-hidden="true">!</span>
                <span>
                  <strong>Alas!</strong> An alarm goes off and the door shuts
                  automatically in 3 seconds.
                </span>
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <p className="rules-text rules-text--lead">
                Ravi is now stuck inside the vault while you are outside.
              </p>
              <p className="rules-text">
                The system does not allow you to open the door without privilege
                access. It also fills the room up with water as a defense mechanism
                in the case of a threat.
              </p>
              <p className="rules-callout rules-callout--info">
                <span className="rules-callout__icon" aria-hidden="true">?</span>
                <span>
                  The only way to communicate with him is his mobile phone, but he
                  only has <strong>20% battery</strong> power. Can you help him escape
                  from the room by guiding Ravi?
                </span>
              </p>
            </>
          )}

          {step === 3 && (
            <ul className="rule-list">
              {RULE_ITEMS.map(({ icon: Icon, text }, i) => (
                <li key={i} className="rule-list__item">
                  <span className="rule-list__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="rule-list__text">{text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rules-actions">
          {step > 1 && (
            <button
              type="button"
              className="user-prev-button"
              onClick={() => setStep((s) => s - 1)}
              disabled={nextLoading}
            >
              <span aria-hidden="true">←</span> Back
            </button>
          )}
          <button
            type="button"
            className="user-next-button"
            onClick={handleNextClick}
            disabled={step === 3 && nextLoading}
          >
            {step === 3 ? (nextLoading ? "Loading…" : "Start Mission") : "Continue"}
            {!(step === 3 && nextLoading) && (
              <span className="user-next-button__arrow" aria-hidden="true">→</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserRules;
